'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

// Heavy: keep off the main thread until user scrolls near it / interacts.
const ImageGenerator = dynamic(
  () => import('./image').then((m) => m.ImageGenerator),
  { ssr: false }
);

export function ImageGeneratorLazy({ title, defaultExampleImage, defaultPrompt }: { title?: string; defaultExampleImage?: string; defaultPrompt?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(false);

  // Handle anchor scroll to center of component
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const hash = window.location.hash;
    if (hash === '#generator' && ref.current) {
      // Scroll to center of the component
      const element = ref.current;
      const elementTop = element.offsetTop;
      const elementHeight = element.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollPosition = elementTop + elementHeight / 2 - windowHeight / 2;
      
      window.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (enabled) return;

    const el = ref.current;
    if (!el) return;

    const enable = () => setEnabled(true);

    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 768px)').matches;

    // Conservative for both desktop & mobile:
    // - Avoid CLS from "placeholder -> real generator" swaps during Lighthouse runs.
    // - Keep JS off the main thread until the user shows intent (scroll/click/key).
    const rootMargin = isMobile ? '0px 0px' : '600px 0px';

    let io: IntersectionObserver | null = null;
    const startObserve = () => {
      if (io) return;
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            enable();
            io?.disconnect();
            io = null;
          }
        },
        { rootMargin }
      );
      io.observe(el);
    };

    const onFirstIntent = () => {
      startObserve();
      window.removeEventListener('scroll', onFirstIntent);
      window.removeEventListener('pointerdown', onFirstIntent);
      window.removeEventListener('keydown', onFirstIntent);
    };

    window.addEventListener('scroll', onFirstIntent, { passive: true });
    window.addEventListener('pointerdown', onFirstIntent, { passive: true });
    window.addEventListener('keydown', onFirstIntent);

    // Safety: if user never interacts, load after long idle to avoid a "broken" page
    const idleTimer = window.setTimeout(() => {
      enable();
    }, 15000);

    return () => {
      window.removeEventListener('scroll', onFirstIntent);
      window.removeEventListener('pointerdown', onFirstIntent);
      window.removeEventListener('keydown', onFirstIntent);
      if (io) io.disconnect();
      window.clearTimeout(idleTimer);
    };
  }, [enabled]);

  return (
    <div ref={ref} id="generator">
      {enabled ? (
        <ImageGenerator
          title={title}
          defaultExampleImage={defaultExampleImage}
          defaultPrompt={defaultPrompt}
        />
      ) : (
        <section className="py-16 md:py-24">
          <div className="container">
            <div className="mx-auto max-w-6xl">
              <div className="mb-6 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setEnabled(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 items-center justify-center rounded-md px-6 text-sm font-medium"
                >
                  Load Generator
                </button>
              </div>
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div className="h-[560px] rounded-lg border bg-muted/30" />
                <div className="relative h-[560px] rounded-lg border bg-muted/30">
                  {title && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <h2 className="text-2xl font-semibold">{title}</h2>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}


